import React, { useContext, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuGroup,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
} from "./ui/dropdown-menu";
import {
  ConstructionIcon,
  Home,
  FileText,
  Calendar,
  CheckSquare,
  BarChart3,
  Building2,
  Phone,
  Camera,
  Settings,
  User,
  LogOut,
  Menu,
  X,
  TrendingUp,
  UserCog,
  List,
  ChevronRight,
  HelpCircle,
  Shield,
  Users,
  ClipboardCheck,
  MessageSquare,
  Sparkles,
  CreditCard,
  LayoutDashboard,
  Briefcase,
  Palette,
  LifeBuoy,
  Receipt,
} from "lucide-react";

const formatRole = (role) => {
  if (role === 'view_only') return 'View Only';
  if (role === 'site_manager') return 'Site Manager';
  return role?.charAt(0).toUpperCase() + role?.slice(1);
};

// Maps a route to its section in the User Manual (/help) for contextual help.
const HELP_MAP = {
  "/": "home",
  "/dashboard": "dashboard",
  "/calendar": "calendar",
  "/planner": "planner",
  "/tasks": "tasks",
  "/expenses": "expenses",
  "/reports": "reports",
  "/contractor-view": "contractor",
  "/phone-records": "phone",
  "/site-photos": "site-photos",
  "/defects": "defects",
  "/clients": "clients",
  "/ohs": "ohs-home",
  "/ohs/attendance": "ohs-attendance",
  "/ohs/inductions": "ohs-inductions",
  "/ohs/safety-walks": "ohs-safety-walks",
  "/ohs/toolbox-talks": "ohs-toolbox-talks",
  "/sites": "sites",
  "/site-managers": "site-managers",
  "/manage-lists": "manage-lists",
  "/overview": "head-office-rollup",
  "/company-profile": "company-profile",
  "/admin/companies": "admin-companies",
  "/admin/branding": "platform-branding",
  "/support": "support",
  "/admin/demo": "demo-admin",
  "/admin/subscriptions": "subscriptions",
};
const helpSectionFor = (pathname) => {
  if (HELP_MAP[pathname]) return HELP_MAP[pathname];
  // match nested/detail routes by longest prefix (e.g. /defects/123)
  const hit = Object.keys(HELP_MAP)
    .filter((p) => p !== "/" && pathname.startsWith(p))
    .sort((a, b) => b.length - a.length)[0];
  return hit ? HELP_MAP[hit] : null;
};

export default function Navbar() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const isPlatformAdmin = user?.role === "admin";
  const isCompanyAdmin = user?.role === "admin" || user?.role === "company_owner";

  const mainMenuItems = [
    { path: "/", label: "Home", icon: Home },
  ];

  const headOfficeItems = isCompanyAdmin
    ? [{ path: "/overview", label: "Head-Office Overview", icon: LayoutDashboard }]
    : [];

  const otherMenuItems = [
    { path: "/defects", label: "Defect Management", icon: Settings },
    ...(["admin", "company_owner", "site_manager"].includes(user?.role)
      ? [{ path: "/work-in-progress", label: "Work In Progress", icon: ClipboardCheck }]
      : []),
    ...(isCompanyAdmin || user?.role === "site_manager"
      ? [{ path: "/client-comms", label: "Client Comms", icon: MessageSquare }]
      : []),
  ];

  const ohsMenuItems = [
    { path: "/ohs/attendance", label: "Daily Site Attendance", icon: Users },
    { path: "/ohs/inductions", label: "Site Induction Records", icon: FileText },
    { path: "/ohs/safety-walks", label: "Safety Walks", icon: ClipboardCheck },
    { path: "/ohs/toolbox-talks", label: "Toolbox Talks", icon: ClipboardCheck },
  ];

  const allSiteMenuItems = [
    { path: "/dashboard", label: "Dashboard", icon: ConstructionIcon },
    { path: "/calendar", label: "Calendar", icon: Calendar },
    { path: "/planner", label: "Planner", icon: TrendingUp },
    { path: "/tasks", label: "Tasks", icon: CheckSquare },
    { path: "/expenses", label: "Expenses", icon: Receipt },
    { path: "/reports", label: "Reports", icon: BarChart3 },
    { path: "/contractor-view", label: "Contractor View", icon: User },
    { path: "/phone-records", label: "Phone Records", icon: Phone },
    { path: "/site-photos", label: "Site Photos", icon: Camera },
  ];

  // Filter out Tasks for view_only users (tasks are site manager specific)
  const canUsePhotos = isCompanyAdmin || user?.role === "site_manager";
  const siteMenuItems = allSiteMenuItems.filter((item) => {
    if (item.path === "/tasks" && user?.role === "view_only") return false;
    if (item.path === "/expenses" && ["view_only", "supervisor"].includes(user?.role)) return false;
    if (item.path === "/site-photos" && !canUsePhotos) return false;
    return true;
  });

  const controlsItems = [
    { path: "/sites", label: "Sites", icon: Building2 },
    { path: "/site-managers", label: "User Control", icon: UserCog },
    { path: "/manage-lists", label: "Manage Lists", icon: List },
    ...(isCompanyAdmin ? [{ path: "/company-profile", label: "Company Profile", icon: Briefcase }] : []),
    ...(isPlatformAdmin ? [{ path: "/admin/demo", label: "Demo & Trials", icon: Sparkles }] : []),
    ...(isPlatformAdmin ? [{ path: "/admin/companies", label: "Companies", icon: Building2 }] : []),
    ...(isPlatformAdmin ? [{ path: "/admin/subscriptions", label: "Subscriptions", icon: CreditCard }] : []),
    ...(isPlatformAdmin ? [{ path: "/admin/branding", label: "Platform Branding", icon: Palette }] : []),
  ];

  const showControls = isCompanyAdmin || user?.role === "supervisor";

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 shadow-sm">
      <div className="flex items-center justify-between h-16 px-4">
        {/* Logo - Clickable to Home */}
        <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
          <img src="/logo.png?v=3" alt="Site Pro App Logo" className="w-8 h-8 object-contain" />
          <h1 className="font-bold text-lg text-slate-800 hidden sm:block">Site Pro App</h1>
        </Link>

        {/* Right Side: User Info and Menu */}
        <div className="flex items-center space-x-4">
          {/* User Info - Desktop */}
          <div className="hidden md:block text-right">
            <p className="font-medium text-slate-900 text-sm">{user?.username}</p>
            <p className="text-xs text-slate-600">{formatRole(user?.role)}</p>
            {user?.email && <p className="text-xs text-slate-400" data-testid="nav-user-email">{user.email}</p>}
          </div>

          {/* Contextual Help button - deep links to this screen's manual section */}
          <Link
            to={(() => { const s = helpSectionFor(location.pathname); return s ? `/help?section=${s}` : "/help"; })()}
            title="Help for this screen"
            data-testid="contextual-help-btn"
            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-slate-200 text-slate-500 hover:text-amber-600 hover:border-amber-300 transition-colors"
          >
            <HelpCircle className="w-5 h-5" />
          </Link>

          {/* Dropdown Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <Menu className="w-4 h-4" />
                <span className="hidden sm:inline">Menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {/* User Info - Mobile */}
              <div className="md:hidden px-2 py-2">
                <p className="font-medium text-slate-900 text-sm">{user?.username}</p>
                <p className="text-xs text-slate-600">{formatRole(user?.role)}</p>
                {user?.email && <p className="text-xs text-slate-400">{user.email}</p>}
              </div>
              <DropdownMenuSeparator className="md:hidden" />

              {/* Main Navigation Items */}
              <DropdownMenuGroup>
                {/* Home */}
                {mainMenuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <DropdownMenuItem
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={isActive ? "bg-amber-50 text-amber-700" : ""}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      <span>{item.label}</span>
                    </DropdownMenuItem>
                  );
                })}
                
                {/* Site Submenu - positioned after Home, before Defect Management */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Building2 className="w-4 h-4 mr-2" />
                    <span>Site</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent>
                      {siteMenuItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;
                        return (
                          <DropdownMenuItem
                            key={item.path}
                            onClick={() => navigate(item.path)}
                            className={isActive ? "bg-amber-50 text-amber-700" : ""}
                          >
                            <Icon className="w-4 h-4 mr-2" />
                            <span>{item.label}</span>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>

                {/* Other top-level items */}
                {otherMenuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <DropdownMenuItem
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={isActive ? "bg-amber-50 text-amber-700" : ""}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      <span>{item.label}</span>
                    </DropdownMenuItem>
                  );
                })}

                {/* OH&S Submenu */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Shield className="w-4 h-4 mr-2" />
                    <span>OH & S</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent>
                      {ohsMenuItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;
                        return (
                          <DropdownMenuItem
                            key={item.path}
                            onClick={() => navigate(item.path)}
                            className={isActive ? "bg-amber-50 text-amber-700" : ""}
                          >
                            <Icon className="w-4 h-4 mr-2" />
                            <span>{item.label}</span>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>

                {/* Head-Office Overview — after OH&S (most users are single-company operators) */}
                {headOfficeItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <DropdownMenuItem
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={isActive ? "bg-amber-50 text-amber-700" : ""}
                      data-testid="nav-head-office-overview"
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      <span>{item.label}</span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuGroup>

              {/* Controls Section - Admin/Supervisor Only */}
              {showControls && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Settings className="w-4 h-4 mr-2" />
                        <span>Controls</span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent>
                          {controlsItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname === item.path;
                            return (
                              <DropdownMenuItem
                                key={item.path}
                                onClick={() => navigate(item.path)}
                                className={isActive ? "bg-amber-50 text-amber-700" : ""}
                              >
                                <Icon className="w-4 h-4 mr-2" />
                                <span>{item.label}</span>
                              </DropdownMenuItem>
                            );
                          })}
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                  </DropdownMenuGroup>
                </>
              )}

              {/* Help & User Manual */}
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => navigate("/help")}
                className={location.pathname === "/help" ? "bg-amber-50 text-amber-700" : ""}
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                <span>Help & User Manual</span>
              </DropdownMenuItem>

              {/* Contact Support — all roles */}
              <DropdownMenuItem
                onClick={() => navigate("/support")}
                className={location.pathname === "/support" ? "bg-amber-50 text-amber-700" : ""}
                data-testid="nav-contact-support"
              >
                <LifeBuoy className="w-4 h-4 mr-2" />
                <span>Contact Support</span>
              </DropdownMenuItem>

              {/* Logout */}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" />
                <span>Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
